import { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Button,
  CircularProgress,
  Typography,
  TextField,
} from "@mui/material";
import { Logo } from "../../../shared/ui/logo";
import {
  useSendSmsCodeMutation,
  useSmsAuthMutation,
} from "../../../entities/auth";
import { useNavigate } from "react-router-dom";
import { ROUTER_PATHS } from "../../../shared/constants";
import { useGetPhoneCodesQuery } from "../../../entities/preset-data";
import { PhoneInput } from "./phone-input";
import { useTranslation } from "react-i18next";
import { localStorageService } from "../../../shared/api";
import { TRANSLATION_KEYS } from "../../../shared/config/i18n";
import { selectMe, useGetMyProfileQuery } from "../../../entities/me";
import { useAppSelector } from "../../../app/store";

export const AuthPage = () => {
  const { t } = useTranslation();
  const me = useAppSelector(selectMe);
  const navigate = useNavigate();
  const [step, setStep] = useState<"phone" | "code">("phone");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [smsCode, setSmsCode] = useState("");
  const [timer, setTimer] = useState(0);
  const [codeError, setCodeError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const { refetch } = useGetMyProfileQuery();
  const [sendSmsCode, { isLoading: isSendingCode }] = useSendSmsCodeMutation();
  const [smsAuth, { isLoading: isAuthenticating }] = useSmsAuthMutation();
  const { isLoading: isLoadingPhoneCodes } = useGetPhoneCodesQuery();

  useEffect(() => {
    if (me) {
      navigate(`/`);
    }
  }, [me, navigate]);

  const handlePhoneChange = (value: string) => {
    setPhoneNumber(value);
  };

  const handleSmsCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 6) {
      setSmsCode(value);
      if (codeError) setCodeError("");
    }
  };

  const getFormattedPhoneForApi = () => {
    return phoneNumber.startsWith("+")
      ? phoneNumber.replace("+", "")
      : `${phoneNumber}`;
  };

  const handleSendCode = async () => {
    const token = await localStorageService.getToken();
    if (!token) {
      return;
    }
    try {
      const formattedPhone = getFormattedPhoneForApi();
      const response = await sendSmsCode({
        phoneNumber: formattedPhone,
        token,
      }).unwrap();

      if (response.result) {
        setPhoneError("");
        setStep("code");
        startTimer();
      } else {
        setPhoneError("Не удалось отправить код. Попробуйте ещё раз.");
      }
    } catch (error) {
      console.error("Failed to send SMS code:", error);
      setPhoneError("Не удалось отправить код. Попробуйте ещё раз.");
    }
  };

  const handleVerifyCode = async () => {
    const token = await localStorageService.getToken();
    if (!token) {
      return;
    }
    try {
      setCodeError("");
      const formattedPhone = getFormattedPhoneForApi();
      const response = await smsAuth({
        phoneNumber: formattedPhone,
        code: smsCode,
        sessionName: "web",
        fingerprint: navigator.userAgent,
        token,
      }).unwrap();

      if (response.result) {
        refetch();
        navigate(`/${ROUTER_PATHS.HOME}`);
      } else {
        setCodeError("Неверный код подтверждения");
      }
    } catch (error) {
      console.error("Failed to verify SMS code:", error);
      setCodeError("Неверный код подтверждения");
    }
  };

  const handleResendCode = () => {
    if (timer === 0) {
      handleSendCode();
    }
  };

  const startTimer = () => {
    setTimer(60);
    const timerInterval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const isPhoneValid = () => {
    return phoneNumber.length >= 11;
  };

  const isCodeValid = () => {
    return smsCode.length >= 4;
  };

  const renderPhoneStep = () => (
    <Box>
      <Typography variant="h5" textAlign="center" sx={{ mb: 1.5 }}>
        Вход в админку
      </Typography>
      <Typography
        variant="body2"
        textAlign="center"
        sx={{ mb: 3, color: "text.secondary" }}
      >
        Введите номер телефона — мы отправим SMS с кодом
      </Typography>

      <PhoneInput value={phoneNumber} onChange={handlePhoneChange} />

      {phoneError && (
        <Typography color="error" variant="body2" sx={{ mt: 1.5 }}>
          {phoneError}
        </Typography>
      )}

      <Button
        fullWidth
        variant="contained"
        onClick={handleSendCode}
        disabled={!isPhoneValid() || isSendingCode || isLoadingPhoneCodes}
        sx={{ py: 1.25, mt: 3 }}
      >
        {isSendingCode || isLoadingPhoneCodes ? (
          <CircularProgress size={22} sx={{ color: "primary.contrastText" }} />
        ) : (
          "Продолжить"
        )}
      </Button>
    </Box>
  );

  const renderCodeStep = () => (
    <>
      <Typography
        variant="h5"
        textAlign="center"
        sx={{ mb: 2, fontWeight: 500 }}
      >
        {t(TRANSLATION_KEYS.AUTH.VERIFY_CODE)}
      </Typography>
      <Typography
        variant="body1"
        textAlign="center"
        sx={{ mb: 3, color: "text.secondary" }}
      >
        {t(TRANSLATION_KEYS.AUTH.ENTER_SMS_CODE)} {getFormattedPhoneForApi()}
      </Typography>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="filled"
          value={smsCode}
          onChange={handleSmsCodeChange}
          placeholder="123456"
          type="tel"
          label={t(TRANSLATION_KEYS.AUTH.VERIFY_CODE)}
          inputProps={{ maxLength: 6 }}
          error={!!codeError}
          helperText={codeError}
        />
      </Box>

      <Button
        fullWidth
        variant="contained"
        onClick={handleVerifyCode}
        disabled={!isCodeValid() || isAuthenticating}
        sx={{
          mt: 2,
        }}
      >
        {isAuthenticating ? (
          <CircularProgress size={22} sx={{ color: "primary.contrastText" }} />
        ) : (
          t(TRANSLATION_KEYS.ACTIONS.CONFIRM)
        )}
      </Button>

      <Button
        fullWidth
        variant="text"
        onClick={handleResendCode}
        disabled={timer > 0}
        sx={{ mt: 2, color: "text.secondary" }}
      >
        {timer > 0
          ? t(TRANSLATION_KEYS.AUTH.RESEND_CODE_TIMER, { seconds: timer })
          : t(TRANSLATION_KEYS.AUTH.RESEND_CODE)}
      </Button>

      <Button
        fullWidth
        variant="text"
        onClick={() => setStep("phone")}
        sx={{ mt: 2, color: "text.secondary" }}
      >
        {t(TRANSLATION_KEYS.ACTIONS.BACK)}
      </Button>
    </>
  );

  return (
    <Box
      sx={{
        height: "100vh",
        width: "100vw",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        position: "fixed",
        top: 0,
        left: 0,
        px: 2,
        backgroundImage:
          "radial-gradient(ellipse at top, rgba(219, 150, 38, 0.12), transparent 55%)",
      }}
    >
      <Paper
        elevation={2}
        sx={{
          borderRadius: 3,
          p: 4,
          width: "100%",
          maxWidth: 440,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <Logo />
        </Box>
        {step === "phone" ? renderPhoneStep() : renderCodeStep()}
      </Paper>
    </Box>
  );
};
